import * as React from 'react';
import { rem } from 'polished';
import { IConfigObj } from '@makemydeal/dr-common-utils';

import { BreakPoint } from '../../types/BreakPoint';
import { IconFactory } from '../IconFactory/IconFactory';

// styles
import styled from 'styled-components';

const tabletBreakpoint = BreakPoint.SCREEN_SM_MIN;

export const StyledAskAQuestionPanel = styled.div`
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: auto;
    flex-direction: row;
    align-items: flex-start;
    text-align: center;

    width: 100%;
    min-width: 250px;
    min-height: auto;
    padding: 1rem;
    margin: 1rem 0;
    position: relative;

    color: #54565a;
    border-color: ${({ theme }) => theme.defaultHrBorderColor};
    border-style: solid;
    border-width: 0;

    @media (min-width: ${tabletBreakpoint}px) {
        flex-direction: column;
        min-height: 224px;
        padding: 1rem 1.25rem;
        margin-top: 1.375rem;
        margin-bottom: 1.375rem;
    }
`;

export const StyledAskAQuestionIconWrapper = styled.div`
    width: 100%;
    text-align: ${({ alignment }) => alignment};
    margin: ${({ alignment, isCounterOfferMobile }) => {
        if (isCounterOfferMobile) {
            return `${rem(0.75)} ${rem(0.5)} ${rem(0.5)} 0`;
        } else {
            return alignment === 'left' ? `${rem(0.75)} ${rem(1)}` : `${rem(0.75)} 0 ${rem(0.5)} 0`;
        }
    }};
    flex: 0 1 32px;
    image,
    svg {
        height: 42px;
        width: 32px;
    }
    @media (min-width: ${tabletBreakpoint}px) {
        margin: 0 auto;
    }
`;

export const StyledCounterOfferTitleWrapper = styled.div`
    width: 100%;
    text-align: ${({ alignment }) => alignment};
    color: ${({ theme }) => theme.commonShareDealHeaderColor};
    margin: ${({ alignment, isCounterOfferMobile }) => {
        if (isCounterOfferMobile) {
            return '0 auto';
        } else {
            return alignment === 'left' ? `${rem(0.75)} ${rem(1)}` : `${rem(0.75)} 0 ${rem(0.5)} 0`;
        }
    }};
    flex: 0 1 32px;
    font-size: ${rem(18)};
    @media (min-width: ${tabletBreakpoint}px) {
        margin: 0 auto;
    }
`;

export const StyledAskAQuestionPanelContent = styled.div`
    width: 100%;
    flex: 5;
    text-align: ${({ alignment }) => alignment};
`;

export const StyledAskAQuestionHeader = styled.div`
    font-size: 1rem;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.commonAskAQuestionHeaderColor};
`;

export const StyledAskAQuestionDescription = styled.div`
    font-size: 0.875rem;
    margin-top: 0;
    padding-top: 0;
    padding-right: 0;
    padding-left: 0;
    padding-bottom: 1.25rem;
    color: ${({ theme }) => theme.commonAskAQuestionDescriptionColor};
`;

export const StyledAskAQuestionLink = styled.div`
    font-weight: ${({ theme }) => theme.linkTextWeight};
    a {
        &:hover {
            color: ${({ theme }) => theme.primaryLinkHoverColor};
            text-decoration: ${({ theme }) => theme.primaryLinkHoverTextDecoration};
        }
        &:active {
            color: ${({ theme }) => theme.primaryLinkActiveColor};
            text-decoration: ${({ theme }) => theme.primaryLinkActiveTextDecoration};
        }
        &:focus {
            color: ${({ theme }) => theme.primaryLinkFocusColor};
            text-decoration: ${({ theme }) => theme.primaryLinkFocusTextDecoration};
        }
        &:disabled {
            opacity: ${({ theme }) => theme.primaryLinkDisabledOpacity};
        }
    }
`;

export const CounterOfferBtnsWrapper = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
`;

export const CounterOfferMobileTopPanel = styled.div`
    display: flex;
    svg {
        height: 50px;
        width: 42px;
    }
`;

export const AskAQuestionButtonComponent = styled.button`
    font-size: ${rem(16)};
    line-height: 1.25;
    margin-bottom: 1rem;
    width: ${({ isMobile }) => (isMobile ? '100%' : '200px')};
    border-color: ${({ theme }) => theme.viewDealButtonColor};
    border-radius: ${({ theme }) => theme.commonViewDealCardButtonBorderRadius};
    background-color: ${({ theme }) => theme.viewDealButtonBackgroundColor};
    font-weight: bold;
    color: ${({ theme }) => theme.viewDealButtonColor};
`;

export const AcceptDealButtonComponent = styled.button`
    font-size: ${rem(16)};
    line-height: 1.25;
    margin-bottom: ${rem(6)};
    width: ${({ isMobile }) => (isMobile ? '100%' : '200px')};
    border-radius: ${({ theme }) => theme.commonViewDealCardButtonBorderRadius};
    color: ${({ isCounterOfferAccepted }) => (isCounterOfferAccepted ? '#fff' : null)};
    background-color: ${({ isCounterOfferAccepted }) => (isCounterOfferAccepted ? '#5cb85c' : null)};
    border-color: ${({ isCounterOfferAccepted }) => (isCounterOfferAccepted ? '#4cae4c' : null)};
    border: ${({ isCounterOfferAccepted }) => (isCounterOfferAccepted ? 'none' : null)};
    font-weight: ${({ isCounterOfferAccepted }) => (isCounterOfferAccepted ? 'none' : 'bold')};
`;

export interface BaseAskAQuestionUIProps {
    description: string;
    linkLabel: string;
    onClick: { () };
    onAcceptDealClick?: { () };
    alignment: string;
    iconName: string;
    staticImages: IConfigObj;
    outerClassName: string;
    id: string;
    isCountered?: boolean;
    isCounterOfferAccepted?: boolean;
    borderDirection?: string;
    /** Property use also to check if it is the counter offer flow */
    counterOfferTitle?: string;
    isMobile: boolean;
}

export class BaseAskAQuestionUI extends React.Component<BaseAskAQuestionUIProps, any> {
    applyBorder = (wrapperStyle, dir) => {
        switch (dir) {
            case 'left':
                return (wrapperStyle = { ...wrapperStyle, borderLeftWidth: '1px' });
            case 'right':
                return (wrapperStyle = { ...wrapperStyle, borderRightWidth: '1px' });
            case 'top':
                return (wrapperStyle = { ...wrapperStyle, borderTopWidth: '1px' });
            case 'bottom':
                return (wrapperStyle = { ...wrapperStyle, borderBottomWidth: '1px' });
            default:
                break;
        }
    };

    calcWrapperStyle = () => {
        const { borderDirection, alignment } = this.props;

        let wrapperStyle = {
            borderLeftWidth: '0',
            borderRightWidth: '0',
            borderTopWidth: '0',
            borderBottomWidth: '0',
            flexDirection: 'column'
        };

        const isBorderDirectionArray = Array.isArray(borderDirection);

        if (alignment === 'left') {
            wrapperStyle.flexDirection = 'row';
        }

        if (borderDirection && isBorderDirectionArray) {
            Array.from(borderDirection).forEach((dir) => {
                wrapperStyle = this.applyBorder(wrapperStyle, dir);
            });
        } else if (borderDirection) {
            wrapperStyle = this.applyBorder(wrapperStyle, borderDirection);
        }

        return wrapperStyle;
    };

    private getCounterOfferTitle() {
        const { alignment, counterOfferTitle, isMobile } = this.props;

        return (
            <StyledCounterOfferTitleWrapper alignment={alignment} isCounterOfferMobile={isMobile} className="common-header">
                {counterOfferTitle}
            </StyledCounterOfferTitleWrapper>
        );
    }

    private getAcceptButton() {
        const { isCountered, isCounterOfferAccepted, isMobile, onAcceptDealClick } = this.props;
        const buttonType = isCounterOfferAccepted ? null : 'btn-primary';
        const testId = isCounterOfferAccepted ? 'accepted' : 'accept';
        const callAction = isCounterOfferAccepted ? null : onAcceptDealClick;
        const displayText = isCounterOfferAccepted ? (
            <>
                <i className="fa fa-check" />
                <span className="btn-text">Accepted</span>
            </>
        ) : (
            'Accept This Offer'
        );
        return (
            !isCountered && (
                <AcceptDealButtonComponent
                    type="button"
                    className={`accept-offer-btn ${buttonType} btn-lg`}
                    data-testid={`${testId}-offer-btn`}
                    isMobile={isMobile}
                    isCounterOfferAccepted={isCounterOfferAccepted}
                    onClick={callAction}
                >
                    {displayText}
                </AcceptDealButtonComponent>
            )
        );
    }

    private getCounterOfferCTAs() {
        const { isMobile, onClick } = this.props;
        const acceptButton = this.getAcceptButton();
        return (
            <CounterOfferBtnsWrapper>
                {acceptButton}
                <AskAQuestionButtonComponent
                    type="button"
                    className="ask-a-question-button btn btn-secondary btn-lg"
                    data-testid="ask-a-question-btn"
                    isMobile={isMobile}
                    onClick={onClick}
                >
                    Ask a Question
                </AskAQuestionButtonComponent>
            </CounterOfferBtnsWrapper>
        );
    }

    renderCounterOfferElms = () => {
        const { staticImages, alignment, description, iconName, isMobile } = this.props;

        return (
            (!isMobile && (
                <>
                    {this.getCounterOfferTitle()}
                    <StyledAskAQuestionPanelContent alignment={alignment}>
                        <StyledAskAQuestionDescription isMobile>{description}</StyledAskAQuestionDescription>
                        {this.getCounterOfferCTAs()}
                    </StyledAskAQuestionPanelContent>
                </>
            )) || (
                <>
                    <div>
                        <CounterOfferMobileTopPanel>
                            <StyledAskAQuestionIconWrapper alignment={alignment} isCounterOfferMobile={isMobile}>
                                <IconFactory staticImages={staticImages} icon={iconName} />
                            </StyledAskAQuestionIconWrapper>
                            <StyledAskAQuestionPanelContent alignment={alignment}>
                                {this.getCounterOfferTitle()}
                                <StyledAskAQuestionDescription isMobile>{description}</StyledAskAQuestionDescription>
                            </StyledAskAQuestionPanelContent>
                        </CounterOfferMobileTopPanel>
                        {this.getCounterOfferCTAs()}
                    </div>
                </>
            )
        );
    };

    renderAskQuestionElms = () => {
        const { staticImages, alignment, description, onClick, linkLabel, iconName } = this.props;

        return (
            <>
                {staticImages && (
                    <StyledAskAQuestionIconWrapper alignment={alignment}>
                        <IconFactory staticImages={staticImages} icon={iconName} />
                    </StyledAskAQuestionIconWrapper>
                )}

                <StyledAskAQuestionPanelContent alignment={alignment}>
                    <StyledAskAQuestionDescription>{description}</StyledAskAQuestionDescription>
                    <StyledAskAQuestionLink>
                        <a className="primary-link" id="ask_a_question_on_click" onClick={onClick}>
                            {linkLabel}
                        </a>
                    </StyledAskAQuestionLink>
                </StyledAskAQuestionPanelContent>
            </>
        );
    };

    render() {
        const wrapperStyle = this.calcWrapperStyle();
        const { outerClassName, id, counterOfferTitle } = this.props;

        return (
            <StyledAskAQuestionPanel id={id} style={wrapperStyle} className={outerClassName}>
                {counterOfferTitle ? this.renderCounterOfferElms() : this.renderAskQuestionElms()}
            </StyledAskAQuestionPanel>
        );
    }
}
